import React from 'react';
import Plotly from 'plotly.js-dist-min';
import createPlotlyComponent from 'react-plotly.js/factory';
import type { CombinedCurveData } from '../utils/pkCurveGen';

const Plot = createPlotlyComponent(Plotly);

type CombinedCurveInput = CombinedCurveData & {
  current_phase?: {
    time: number[];
    concentration: number[];
    concentration_p025?: number[];
    concentration_p975?: number[];
    concentration_p05?: number[];
    concentration_p95?: number[];
    n_subjects?: number;
  };
  optimized_phase?: {
    time: number[];
    concentration: number[];
    concentration_p025?: number[];
    concentration_p975?: number[];
    concentration_p05?: number[];
    concentration_p95?: number[];
    n_subjects?: number;
  };
  continuation_phase?: {
    time: number[];
    concentration: number[];
    concentration_p025?: number[];
    concentration_p975?: number[];
    concentration_p05?: number[];
    concentration_p95?: number[];
    n_subjects?: number;
  };
  switch_time_h?: number;
};

export interface TDMPoint {
  time: number;       // absolute time on curve (h)
  concentration: number; // measured concentration (mg/L)
  label?: string;     // optional label (e.g. "TDM #1")
  before_next_dose_h?: number;
}

export interface PKFormulaCurveContext {
  strategy_key?: string;
  mode?: string;
  interval_h?: number;
  infusion_h?: number;
  display_window_h?: [number, number] | number[];
  dose_times_h?: number[];
  infusion_windows_h?: Array<[number, number] | number[]>;
  sample_points?: Array<{
    key?: string;
    time_h: number;
    concentration: number;
    before_next_dose_h?: number;
  }>;
  curve?: {
    time: number[];
    concentration: number[];
  };
}

export interface DoseEventMarker {
  time: number;
  dose?: number;
  eventType: 'regular' | 'remedial' | 'missed' | 'skipped' | string;
  label: string;
  doseLabel?: string;
  decisionIndex?: number;
}

export interface PKComparisonCurve {
  time: number[];
  concentration: number[];
  label: string;
  color?: string;
}

interface PKCurveProps {
  simulationData?: {
    time: number[];
    concentration: number[];
    concentrationP025?: number[];
    concentrationP975?: number[];
    concentration_p025?: number[];
    concentration_p975?: number[];
    concentrationP05?: number[];
    concentrationP95?: number[];
    concentration_p05?: number[];
    concentration_p95?: number[];
    concentrationMin?: number[];
    concentrationMax?: number[];
    concentration_min?: number[];
    concentration_max?: number[];
    n_subjects?: number;
    dose_events?: Array<Record<string, any>>;
    phase_markers?: {
      loading_start_h?: number;
      maintenance_start_h?: number;
    };
  };
  /** 分段对比曲线 (TDM优化模式), 优先于 simulationData */
  combinedCurveData?: CombinedCurveInput | null;
  targetRange?: {
    troughMin?: number;
    troughMax?: number;
  };
  /** MIC reference line, expressed in micUnit. */
  mic?: number;
  showMicLine?: boolean;
  /** Nephrotoxicity Cmin threshold, expressed in concentrationUnit. */
  nephrotoxicityCmin?: number;
  concentrationUnit?: string;
  micUnit?: string;
  /** TDM 实测血药浓度点 */
  tdmPoints?: TDMPoint[];
  /** Meropenem formula-only steady-state display context. */
  formulaCurveContext?: PKFormulaCurveContext | null;
  /** 给药事件标记，用于补服/漏服策略曲线。 */
  doseEvents?: DoseEventMarker[];
  /** Loading-to-maintenance transition markers returned by the model service. */
  phaseMarkers?: {
    loadingStartH?: number;
    maintenanceStartH?: number;
    loading_start_h?: number;
    maintenance_start_h?: number;
  };
  /** 以研究图的粗黑线标出 p05/p95 包络计入 deviation time 的时段。 */
  showDeviationSegments?: boolean;
  /** Optional single counterfactual median; uncertainty stays on the selected strategy. */
  comparisonCurve?: PKComparisonCurve;
  /** Optional visible time window; simulation and deviation metrics remain unchanged. */
  timeRange?: [number, number];
  /** Let Plotly autoscale both axes for formula-method curves. */
  autoScale?: boolean;
  /** Selected remedial strategy shown on the curve and in the report. */
  strategyLabel?: string;
}

type CurveSeries = {
  time?: number[];
  concentration?: number[];
  concentrationP025?: number[];
  concentrationP975?: number[];
  concentration_p025?: number[];
  concentration_p975?: number[];
  concentrationP05?: number[];
  concentrationP95?: number[];
  concentration_p05?: number[];
  concentration_p95?: number[];
  concentrationMin?: number[];
  concentrationMax?: number[];
  concentration_min?: number[];
  concentration_max?: number[];
  n_subjects?: number;
};

const buildThresholdSegments = (
  time: number[],
  values: number[],
  threshold: number,
  direction: 'above' | 'below',
  evaluationStart = 0,
) => {
  const x: Array<number | null> = [];
  const y: Array<number | null> = [];
  const outside = (value: number) => direction === 'above' ? value > threshold : value < threshold;
  for (let index = 0; index < time.length - 1; index += 1) {
    let t0 = time[index];
    const t1 = time[index + 1];
    let v0 = values[index];
    const v1 = values[index + 1];
    if (![t0, t1, v0, v1, threshold].every(Number.isFinite) || t1 <= evaluationStart || t1 <= t0) continue;
    if (t0 < evaluationStart) {
      const fraction = (evaluationStart - t0) / (t1 - t0);
      v0 += (v1 - v0) * fraction;
      t0 = evaluationStart;
    }
    const outside0 = outside(v0);
    const outside1 = outside(v1);
    if (!outside0 && !outside1) continue;
    let segmentStart = t0;
    let segmentEnd = t1;
    let segmentStartValue = v0;
    let segmentEndValue = v1;
    if (outside0 !== outside1 && v1 !== v0) {
      const crossing = t0 + ((threshold - v0) / (v1 - v0)) * (t1 - t0);
      if (outside0) {
        segmentEnd = crossing;
        segmentEndValue = threshold;
      } else {
        segmentStart = crossing;
        segmentStartValue = threshold;
      }
    }
    if (segmentEnd > segmentStart) {
      x.push(segmentStart, segmentEnd, null);
      y.push(segmentStartValue, segmentEndValue, null);
    }
  }
  return { x, y, hasSegments: x.length > 0 };
};

const CURVE_COLORS = {
  current: '#2563A6',
  currentSoft: '#93C5FD',
  optimized: '#16A34A',
  target: '#16A34A',
  warning: '#C5A55A',
  danger: '#EF4444',
  mic: '#7C3AED',
  text: '#0B2545',
};

// Keep the on-screen legend aligned with the clinical report.  The compact
// swatch still shows dashed thresholds clearly while leaving labels room.
const PK_CURVE_LEGEND_FONT_SIZE = 8.5;
const PK_CURVE_LEGEND_ITEM_WIDTH = 36;

const getPredictionBand = (series?: CurveSeries | null) => {
  if (!series?.time?.length) return null;
  const lower025 = series.concentrationP025 || series.concentration_p025;
  const upper975 = series.concentrationP975 || series.concentration_p975;
  if (
    Array.isArray(lower025)
    && Array.isArray(upper975)
    && lower025.length === series.time.length
    && upper975.length === series.time.length
  ) {
    return { lower: lower025, upper: upper975, label: '人群 2.5%–97.5% 区间' };
  }

  const lower05 = series.concentrationP05 || series.concentration_p05;
  const upper95 = series.concentrationP95 || series.concentration_p95;
  if (
    Array.isArray(lower05)
    && Array.isArray(upper95)
    && lower05.length === series.time.length
    && upper95.length === series.time.length
  ) {
    return { lower: lower05, upper: upper95, label: '人群 5%–95% 区间' };
  }
  return null;
};

const pushPredictionBand = (
  traces: any[],
  time: number[],
  band: ReturnType<typeof getPredictionBand>,
  name: string,
  fillcolor: string,
  legendrank: number,
) => {
  if (!band) return;
  traces.push({
    x: time,
    y: band.upper,
    type: 'scatter',
    mode: 'lines',
    name: `${name} upper`,
    line: { color: 'rgba(0,0,0,0)', width: 0 },
    hoverinfo: 'skip',
    showlegend: false,
    legendrank,
    legendgroup: 'intervals',
  });
  traces.push({
    x: time,
    y: band.lower,
    type: 'scatter',
    mode: 'lines',
    name: `${name}：${band.label}`,
    line: { color: 'rgba(0,0,0,0)', width: 0 },
    fill: 'tonexty',
    fillcolor,
    legendrank,
    legendgroup: 'intervals',
  });
};

const resolveTimeTickStep = (tMin: number, tMax: number): number => {
  const span = Math.max(tMax - tMin, 0);
  if (span <= 72) return 12;
  if (span <= 144) return 24;
  return 48;
};

const PKCurve: React.FC<PKCurveProps> = ({
  simulationData: inputSimulationData,
  combinedCurveData,
  targetRange = {},
  mic,
  showMicLine = false,
  nephrotoxicityCmin,
  concentrationUnit = 'mg/L',
  micUnit = concentrationUnit,
  tdmPoints,
  formulaCurveContext,
  doseEvents,
  phaseMarkers,
  showDeviationSegments = false,
  comparisonCurve,
  timeRange,
  autoScale = false,
  strategyLabel,
}) => {
  const simulationData: PKCurveProps['simulationData'] = inputSimulationData || (
    formulaCurveContext?.curve?.time?.length && formulaCurveContext.curve.concentration?.length
      ? formulaCurveContext.curve as NonNullable<PKCurveProps['simulationData']>
      : undefined
  );
  const isMeropenemFormulaDisplay = formulaCurveContext?.mode === 'steady_state_two_intervals';
  const plotContainerRef = React.useRef<HTMLDivElement>(null);
  const [plotContainerWidth, setPlotContainerWidth] = React.useState(900);

  React.useEffect(() => {
    const container = plotContainerRef.current;
    if (!container) return undefined;
    const updateWidth = () => setPlotContainerWidth(container.getBoundingClientRect().width || 900);
    updateWidth();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const normalizedCombined = combinedCurveData ? {
    currentPhase: combinedCurveData.currentPhase || combinedCurveData.current_phase || { time: [], concentration: [] },
    optimizedPhase: combinedCurveData.optimizedPhase || combinedCurveData.optimized_phase || { time: [], concentration: [] },
    continuationPhase: combinedCurveData.continuationPhase || combinedCurveData.continuation_phase || { time: [], concentration: [] },
    switchTimeH: combinedCurveData.switchTimeH ?? combinedCurveData.switch_time_h ?? 0,
  } : null;

  // normalize camelCase/snake_case combined curve payloads
  const hasCombined = normalizedCombined
    && normalizedCombined.currentPhase?.time?.length > 0
    && normalizedCombined.optimizedPhase?.time?.length > 0;

  // 回退: 无分段数据时使用单曲线
  if (!hasCombined && (!simulationData || !simulationData.time || !simulationData.concentration)) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无模拟数据</div>;
  }

  const traces: any[] = [];
  let allTimes: number[] = [];
  let allConcentrations: number[] = [];

  if (hasCombined) {
    const { currentPhase, optimizedPhase, continuationPhase } = normalizedCombined!;

    // 当前方案延续 (虚线浅蓝, 放底层)
    if (continuationPhase?.time?.length > 0) {
      pushPredictionBand(
        traces,
        continuationPhase.time,
        getPredictionBand(continuationPhase),
        '原方案延续',
        'rgba(147, 197, 253, 0.14)',
        30,
      );
      traces.push({
        x: continuationPhase.time,
        y: continuationPhase.concentration,
        type: 'scatter',
        mode: 'lines',
        name: '原方案延续',
        line: { color: CURVE_COLORS.currentSoft, width: 1.5, dash: 'dash' },
        legendrank: 3,
        legendgroup: 'regimens',
      });
    }

    // Phase 1: 当前方案历史 (实线中蓝)
    pushPredictionBand(
      traces,
      currentPhase.time,
      getPredictionBand(currentPhase),
      '当前方案',
      'rgba(37, 99, 166, 0.16)',
      10,
    );
    traces.push({
      x: currentPhase.time,
      y: currentPhase.concentration,
      type: 'scatter',
      mode: 'lines',
      name: '当前方案',
      line: { color: CURVE_COLORS.current, width: 2.5 },
      legendrank: 1,
      legendgroup: 'regimens',
    });

    // Phase 2: 优化方案 (实线绿色)
    pushPredictionBand(
      traces,
      optimizedPhase.time,
      getPredictionBand(optimizedPhase),
      '调整后方案',
      'rgba(22, 163, 74, 0.16)',
      20,
    );
    traces.push({
      x: optimizedPhase.time,
      y: optimizedPhase.concentration,
      type: 'scatter',
      mode: 'lines',
      name: '调整后方案',
      line: { color: CURVE_COLORS.optimized, width: 2.5 },
      legendrank: 2,
      legendgroup: 'regimens',
    });

    allTimes = [
      ...currentPhase.time,
      ...optimizedPhase.time,
      ...(continuationPhase?.time || []),
    ];
    allConcentrations = [
      ...currentPhase.concentration,
      ...optimizedPhase.concentration,
      ...(continuationPhase?.concentration || []),
      ...(getPredictionBand(currentPhase)?.lower || []),
      ...(getPredictionBand(currentPhase)?.upper || []),
      ...(getPredictionBand(optimizedPhase)?.lower || []),
      ...(getPredictionBand(optimizedPhase)?.upper || []),
      ...(getPredictionBand(continuationPhase)?.lower || []),
      ...(getPredictionBand(continuationPhase)?.upper || []),
    ];
  } else {
    const { time, concentration } = simulationData!;
    const concentrationP05 = simulationData!.concentrationP025 || simulationData!.concentration_p025 || simulationData!.concentrationP05 || simulationData!.concentration_p05;
    const concentrationP95 = simulationData!.concentrationP975 || simulationData!.concentration_p975 || simulationData!.concentrationP95 || simulationData!.concentration_p95;
    const concentrationMin = simulationData!.concentrationMin || simulationData!.concentration_min;
    const concentrationMax = simulationData!.concentrationMax || simulationData!.concentration_max;
    const isPreferredPredictionBand = Boolean(simulationData!.concentrationP025 || simulationData!.concentration_p025);
    const hasPredictionBand = Array.isArray(concentrationP05)
      && Array.isArray(concentrationP95)
      && concentrationP05.length === time.length
      && concentrationP95.length === time.length;
    const lowerBand = hasPredictionBand ? concentrationP05 as number[] : [];
    const upperBand = hasPredictionBand ? concentrationP95 as number[] : [];
    const hasFullRange = Array.isArray(concentrationMin)
      && Array.isArray(concentrationMax)
      && concentrationMin.length === time.length
      && concentrationMax.length === time.length;
    const fullLowerBand = hasFullRange ? concentrationMin as number[] : [];
    const fullUpperBand = hasFullRange ? concentrationMax as number[] : [];
    if (hasFullRange) {
      traces.push({
        x: time,
        y: fullUpperBand,
        type: 'scatter',
        mode: 'lines',
        name: 'Monte Carlo 100% upper',
        line: { color: 'rgba(100, 116, 139, 0)', width: 0 },
        hoverinfo: 'skip',
        showlegend: false,
      });
      traces.push({
        x: time,
        y: fullLowerBand,
        type: 'scatter',
        mode: 'lines',
        name: '100% 人群范围（仅展示）',
        line: { color: 'rgba(100, 116, 139, 0)', width: 0 },
        fill: 'tonexty',
        fillcolor: 'rgba(148, 163, 184, 0.10)',
        legendrank: 30,
        legendgroup: 'intervals',
      });
    }
    if (hasPredictionBand) {
      traces.push({
        x: time,
        y: upperBand,
        type: 'scatter',
        mode: 'lines',
        name: 'Monte Carlo 95%',
        line: { color: 'rgba(37, 99, 166, 0)', width: 0 },
        hoverinfo: 'skip',
        showlegend: false,
      });
      traces.push({
        x: time,
        y: lowerBand,
        type: 'scatter',
        mode: 'lines',
        name: isPreferredPredictionBand ? '人群 2.5%–97.5% 区间' : '人群 5%–95% 区间',
        line: { color: 'rgba(37, 99, 166, 0)', width: 0 },
        fill: 'tonexty',
        fillcolor: 'rgba(37, 99, 235, 0.14)',
        legendrank: 10,
        legendgroup: 'intervals',
      });
    }
    traces.push({
      x: time,
      y: concentration,
      type: 'scatter',
      mode: 'lines',
      name: hasPredictionBand && simulationData!.n_subjects
        ? `选定方案中位浓度 (n=${simulationData!.n_subjects})`
        : '选定方案药物浓度',
      connectgaps: false,
      line: { color: CURVE_COLORS.current, width: 2.3 },
      legendgroup: 'regimens',
      legendrank: 1,
    });
    if (showDeviationSegments) {
      const upper = targetRange.troughMax;
      const lower = targetRange.troughMin;
      const upperDeviation = typeof upper === 'number' && hasPredictionBand
        ? buildThresholdSegments(time, upperBand, upper, 'above')
        : { x: [], y: [], hasSegments: false };
      const lowerDeviation = typeof lower === 'number' && hasPredictionBand
        ? buildThresholdSegments(time, lowerBand, lower, 'below')
        : { x: [], y: [], hasSegments: false };
      if (upperDeviation.hasSegments) {
        traces.push({
          x: upperDeviation.x,
          y: upperDeviation.y,
          type: 'scatter',
          mode: 'lines',
          name: '高于治疗窗',
          connectgaps: false,
          line: { color: '#DC2626', width: 3.5 },
          hovertemplate: '高于治疗窗<br>时间 %{x:.2f} h<extra></extra>',
          legendrank: 12,
          legendgroup: 'thresholds',
        });
      }
      if (lowerDeviation.hasSegments) {
        traces.push({
          x: lowerDeviation.x,
          y: lowerDeviation.y,
          type: 'scatter',
          mode: 'lines',
          name: '低于治疗窗',
          connectgaps: false,
          line: { color: '#D97706', width: 3.5 },
          hovertemplate: '低于治疗窗<br>时间 %{x:.2f} h<extra></extra>',
          legendrank: 13,
          legendgroup: 'thresholds',
        });
      }
    }
    allTimes = time;
    allConcentrations = [
      ...concentration,
      ...(hasPredictionBand ? [...lowerBand, ...upperBand] : []),
      ...(hasFullRange ? [...fullLowerBand, ...fullUpperBand] : []),
    ];
  }

  if (
    comparisonCurve
    && comparisonCurve.time.length === comparisonCurve.concentration.length
    && comparisonCurve.time.length > 0
  ) {
    traces.push({
      x: comparisonCurve.time,
      y: comparisonCurve.concentration,
      type: 'scatter',
      mode: 'lines',
      name: comparisonCurve.label,
      line: { color: comparisonCurve.color || '#64748B', width: 1.5, dash: 'dash' },
      hovertemplate: `${comparisonCurve.label}<br>时间 %{x:.2f} h<br>浓度 %{y:.3f} ${concentrationUnit}<extra></extra>`,
      legendrank: 18,
      legendgroup: 'regimens',
    });
    allTimes = [...allTimes, ...comparisonCurve.time];
    allConcentrations = [...allConcentrations, ...comparisonCurve.concentration];
  }

  const dataTMin = allTimes.length > 0 ? Math.min(...allTimes) : 0;
  const dataTMax = allTimes.length > 0 ? Math.max(...allTimes) : 24;
  const hasCustomTimeRange = !autoScale && Boolean(
    timeRange
    && Number.isFinite(timeRange[0])
    && Number.isFinite(timeRange[1])
    && timeRange[1] > timeRange[0],
  );
  const tMin = hasCustomTimeRange ? Number(timeRange![0]) : dataTMin;
  const tMax = hasCustomTimeRange ? Number(timeRange![1]) : dataTMax;
  const formulaInterval = Number(formulaCurveContext?.interval_h);
  const timeTickStep = isMeropenemFormulaDisplay && Number.isFinite(formulaInterval) && formulaInterval > 0
    ? formulaInterval
    : resolveTimeTickStep(tMin, tMax);
  const timeTickStart = isMeropenemFormulaDisplay ? 0 : Math.floor(tMin / timeTickStep) * timeTickStep;
  const thresholdValues = [
    targetRange.troughMin,
    targetRange.troughMax,
    nephrotoxicityCmin,
    showMicLine ? mic : undefined,
  ].filter((v): v is number => typeof v === 'number' && v > 0);
  const visibleCurveConcentrations: number[] = [];
  if (hasCustomTimeRange && simulationData?.time?.length) {
    const visibleIndices = simulationData.time
      .map((timeValue, index) => ({ timeValue, index }))
      .filter(({ timeValue }) => timeValue >= tMin - 1e-9 && timeValue <= tMax + 1e-9)
      .map(({ index }) => index);
    const visibleSeries = [
      simulationData.concentration,
      simulationData.concentrationP025,
      simulationData.concentration_p025,
      simulationData.concentrationP975,
      simulationData.concentration_p975,
      simulationData.concentrationP05,
      simulationData.concentration_p05,
      simulationData.concentrationP95,
      simulationData.concentration_p95,
      simulationData.concentrationMin,
      simulationData.concentration_min,
      simulationData.concentrationMax,
      simulationData.concentration_max,
    ];
    visibleSeries.forEach((series) => {
      if (!Array.isArray(series) || series.length !== simulationData.time?.length) return;
      visibleIndices.forEach((index) => {
        if (Number.isFinite(series[index])) visibleCurveConcentrations.push(Number(series[index]));
      });
    });
    if (comparisonCurve && comparisonCurve.time.length === comparisonCurve.concentration.length) {
      comparisonCurve.time.forEach((timeValue, index) => {
        if (timeValue >= tMin - 1e-9 && timeValue <= tMax + 1e-9
          && Number.isFinite(comparisonCurve.concentration[index])) {
          visibleCurveConcentrations.push(Number(comparisonCurve.concentration[index]));
        }
      });
    }
  }
  const tdmConcentrations = (tdmPoints || [])
    .filter((point) => !hasCustomTimeRange || (point.time >= tMin - 1e-9 && point.time <= tMax + 1e-9))
    .map((p) => p.concentration)
    .filter((v) => Number.isFinite(v));
  const yCurveCandidates = visibleCurveConcentrations.length > 0
    ? visibleCurveConcentrations
    : allConcentrations;
  const yAxisMaxCandidate = Math.max(
    0,
    ...yCurveCandidates.filter((v) => Number.isFinite(v)),
    ...tdmConcentrations,
    ...thresholdValues,
  );
  const yAxisUpper = yAxisMaxCandidate > 0 ? Number((yAxisMaxCandidate * 1.18).toFixed(3)) : 10;

  const shapes: any[] = [];
  const annotations: any[] = [];

  if (isMeropenemFormulaDisplay) {
    const doseTimes = (formulaCurveContext?.dose_times_h || [])
      .map(Number)
      .filter((value) => Number.isFinite(value) && value >= tMin - 1e-9 && value <= tMax + 1e-9);
    doseTimes.forEach((doseTime, index) => {
      if (index === 0) return;
      shapes.push({
        type: 'line',
        x0: doseTime,
        x1: doseTime,
        y0: 0,
        y1: 1,
        yref: 'paper',
        layer: 'above',
        line: { color: '#64748B', width: 1.3, dash: index === doseTimes.length - 1 ? 'dash' : 'dot' },
      });
      const intervalNumber = Number(formulaCurveContext?.interval_h);
      const intervalValue = Number.isFinite(intervalNumber) ? intervalNumber : null;
      annotations.push({
        x: doseTime,
        y: 1,
        yref: 'paper',
        text: index === doseTimes.length - 1
          ? '\u4e0b\u4e00\u6b21\u7ed9\u836f'
          : `\u91cd\u590d\u95f4\u9694 / \u03c4=${intervalValue == null ? '' : intervalValue.toFixed(2)} h`,
        showarrow: false,
        xanchor: index === doseTimes.length - 1 ? 'right' : 'left',
        yanchor: 'bottom',
        font: { size: 10, color: '#475569' },
      });
    });

    (formulaCurveContext?.infusion_windows_h || []).forEach((window) => {
      const x0 = Number(window?.[0]);
      const x1 = Number(window?.[1]);
      if (!Number.isFinite(x0) || !Number.isFinite(x1) || x1 <= x0) return;
      if (x1 < tMin || x0 > tMax) return;
      shapes.push({
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: Math.max(tMin, x0),
        x1: Math.min(tMax, x1),
        y0: 0,
        y1: 1,
        fillcolor: 'rgba(245, 158, 11, 0.08)',
        line: { width: 0 },
        layer: 'below',
      });
    });
  }

  const loadingStart = Number(phaseMarkers?.loadingStartH ?? phaseMarkers?.loading_start_h);
  const maintenanceStart = Number(
    phaseMarkers?.maintenanceStartH ?? phaseMarkers?.maintenance_start_h,
  );
  if (Number.isFinite(maintenanceStart) && maintenanceStart > tMin && maintenanceStart < tMax) {
    const loadingX0 = Number.isFinite(loadingStart) ? Math.max(tMin, loadingStart) : tMin;
    shapes.push({
      type: 'rect', xref: 'x', yref: 'paper', x0: loadingX0, x1: maintenanceStart,
      y0: 0, y1: 1, fillcolor: 'rgba(245, 158, 11, 0.08)',
      line: { width: 0 }, layer: 'below',
    });
    shapes.push({
      type: 'line', x0: maintenanceStart, x1: maintenanceStart,
      y0: 0, y1: 1, yref: 'paper', layer: 'above',
      line: { color: '#D97706', width: 1.5, dash: 'dot' },
    });
    annotations.push({
      x: (loadingX0 + maintenanceStart) / 2, y: 1, yref: 'paper',
      text: '负荷期', showarrow: false,
      font: { size: 10, color: '#B45309' }, yanchor: 'bottom',
    });
    annotations.push({
      x: maintenanceStart, y: 1, yref: 'paper', text: '维持期开始',
      showarrow: false, xanchor: 'left', yanchor: 'bottom',
      font: { size: 10, color: '#92400E' },
    });
  }

  const visibleDoseEvents = (doseEvents || []).filter((event) => (
    Number.isFinite(event.time) && event.time >= tMin - 1e-9 && event.time <= tMax + 1e-9
  ));
  const doseEventStyles: Record<string, { name: string; color: string; symbol: string }> = {
    loading: { name: '负荷剂量', color: '#D97706', symbol: 'diamond' },
    maintenance: { name: '维持剂量', color: '#2563A6', symbol: 'circle' },
    regular: { name: '常规剂量', color: '#2563A6', symbol: 'circle' },
    remedial: { name: '补服剂量', color: '#D97706', symbol: 'triangle-up' },
    missed: { name: '漏服剂量', color: '#DC2626', symbol: 'circle-open' },
    skipped: { name: '策略跳过剂量', color: '#64748B', symbol: 'x' },
  };
  Object.entries(doseEventStyles).forEach(([eventType, style]) => {
    const events = visibleDoseEvents.filter((event) => event.eventType === eventType);
    if (!events.length) return;
    traces.push({
      x: events.map((event) => event.time),
      y: events.map(() => yAxisUpper * 0.025),
      type: 'scatter',
      mode: 'markers',
      name: style.name,
      marker: {
        color: style.color,
        size: eventType === 'missed' ? 11 : eventType === 'remedial' ? 10 : 8,
        symbol: style.symbol,
        line: { color: eventType === 'missed' ? style.color : '#FFFFFF', width: 1.5 },
      },
      customdata: events.map((event) => [event.label, event.doseLabel || '']),
      hovertemplate: '%{customdata[0]}<br>时间 %{x:.2f} h<br>%{customdata[1]}<extra></extra>',
      legendrank: 20,
      legendgroup: 'events',
    });
  });
  visibleDoseEvents
    .filter((event) => event.eventType === 'missed')
    .forEach((event) => {
      annotations.push({
        x: event.time,
        y: -0.11,
        yref: 'paper',
        text: '漏服',
        showarrow: false,
        font: { size: 10, color: '#B91C1C', family: 'inherit' },
        xanchor: 'center',
        yanchor: 'top',
      });
    });

  const immediateDoseEvents = visibleDoseEvents.filter((event) => (
    event.decisionIndex === 1 && Number(event.dose || 0) > 0
  ));
  immediateDoseEvents.forEach((event) => {
    const overlapsMissed = visibleDoseEvents.some((candidate) => (
      candidate.eventType === 'missed' && Math.abs(candidate.time - event.time) <= 1e-9
    ));
    annotations.push({
      x: event.time,
      y: overlapsMissed ? -0.19 : -0.11,
      yref: 'paper',
      text: '即刻',
      showarrow: false,
      font: { size: 10, color: '#B45309', family: 'inherit' },
      xanchor: 'center',
      yanchor: 'top',
    });
  });

  if (strategyLabel) {
    annotations.push({
      x: 0,
      y: 1.08,
      xref: 'paper',
      yref: 'paper',
      text: strategyLabel,
      showarrow: false,
      font: { size: 11, color: '#0B2545', family: 'inherit' },
      xanchor: 'left',
      yanchor: 'bottom',
      bgcolor: 'rgba(246,255,237,0.92)',
      bordercolor: '#B7EB8F',
      borderwidth: 1,
      borderpad: 5,
    });
  }

  if (showMicLine && mic != null && mic > 0) {
    shapes.push({
      type: 'rect',
      xref: 'x',
      yref: 'y',
      x0: tMin,
      x1: tMax,
      y0: 0,
      y1: Math.min(mic, yAxisUpper),
      fillcolor: 'rgba(245, 158, 11, 0.10)',
      line: { width: 0 },
      layer: 'below',
    });
  }

  if (
    targetRange.troughMin != null
    && targetRange.troughMax != null
    && targetRange.troughMin > 0
    && targetRange.troughMax > targetRange.troughMin
  ) {
    shapes.push({
      type: 'rect',
      xref: 'x',
      yref: 'y',
      x0: tMin,
      x1: tMax,
      y0: targetRange.troughMin,
      y1: targetRange.troughMax,
      fillcolor: 'rgba(16, 185, 129, 0.08)',
      line: { width: 0 },
      layer: 'below',
    });
    traces.splice(0, 0,
      {
        x: [tMin, tMax],
        y: [targetRange.troughMin, targetRange.troughMin],
        type: 'scatter',
        mode: 'lines',
        name: '治疗窗下限',
        line: { color: '#0369A1', width: 1.6, dash: 'dash' },
        hovertemplate: `治疗窗下限 %{y:.3f} ${concentrationUnit}<extra></extra>`,
        legendrank: 6,
        legendgroup: 'thresholds',
      },
      {
        x: [tMin, tMax],
        y: [targetRange.troughMax, targetRange.troughMax],
        type: 'scatter',
        mode: 'lines',
        name: '治疗窗上限',
        line: { color: '#0F766E', width: 1.6, dash: 'dash' },
        hovertemplate: `治疗窗上限 %{y:.3f} ${concentrationUnit}<extra></extra>`,
        legendrank: 7,
        legendgroup: 'thresholds',
      },
    );
  }

  if (nephrotoxicityCmin != null && nephrotoxicityCmin > 0 && nephrotoxicityCmin < yAxisUpper) {
    shapes.push({
      type: 'rect',
      xref: 'x',
      yref: 'y',
      x0: tMin,
      x1: tMax,
      y0: nephrotoxicityCmin,
      y1: yAxisUpper,
      fillcolor: 'rgba(239, 68, 68, 0.09)',
      line: { width: 0 },
      layer: 'below',
    });
  }

  // 毒性风险线
  if (nephrotoxicityCmin != null && nephrotoxicityCmin > 0) {
    traces.push({
      x: [tMin, tMax], y: [nephrotoxicityCmin, nephrotoxicityCmin],
      type: 'scatter', mode: 'lines',
      name: `毒性警戒 (${nephrotoxicityCmin} ${concentrationUnit})`,
      line: { color: CURVE_COLORS.danger, width: 1.5, dash: 'dot' },
      legendgroup: 'thresholds',
      legendrank: 8,
    });
  }

  // MIC 参考线
  if (showMicLine && mic != null && mic > 0) {
    traces.push({
      x: [tMin, tMax], y: [mic, mic],
      type: 'scatter', mode: 'lines',
      name: `MIC = ${mic} ${micUnit}`,
      line: { color: CURVE_COLORS.mic, width: 1.5, dash: 'dashdot' },
      legendgroup: 'thresholds',
      legendrank: 9,
    });
  }

  // TDM 实测血药浓度散点
  if (tdmPoints && tdmPoints.length > 0) {
    const tdmColors = tdmPoints.map((p) => {
      const conc = p.concentration;
      if (nephrotoxicityCmin != null && nephrotoxicityCmin > 0 && conc >= nephrotoxicityCmin) return '#EF4444';
      if (targetRange.troughMax != null && targetRange.troughMax > 0 && conc > targetRange.troughMax) return '#F59E0B';
      if (targetRange.troughMin != null && targetRange.troughMin > 0 && conc < targetRange.troughMin) return '#3B82F6';
      if (
        targetRange.troughMin != null
        && targetRange.troughMax != null
        && targetRange.troughMin > 0
        && targetRange.troughMax > targetRange.troughMin
        && conc >= targetRange.troughMin
        && conc <= targetRange.troughMax
      ) return '#16A34A';
      return '#EF4444';
    });
    traces.push({
      x: tdmPoints.map(p => p.time),
      y: tdmPoints.map(p => p.concentration),
      type: 'scatter', mode: 'markers+text',
      name: 'TDM实测浓度',
      marker: { color: tdmColors, size: 10, symbol: 'diamond', line: { color: '#fff', width: 2 } },
      text: tdmPoints.map(p => `${p.concentration}`),
      textposition: 'top center',
      textfont: { size: 11, color: '#9F1239' },
      customdata: isMeropenemFormulaDisplay
        ? tdmPoints.map((p) => [p.label || '\u6837\u672c', p.before_next_dose_h])
        : undefined,
      hovertemplate: isMeropenemFormulaDisplay
        ? `%{customdata[0]}<br>\u65f6\u95f4 %{x:.2f} h<br>\u6d53\u5ea6 %{y:.3f} ${concentrationUnit}<br>\u8ddd\u4e0b\u6b21\u7ed9\u836f %{customdata[1]:.2f} h<extra></extra>`
        : undefined,
      legendgroup: 'events',
      legendrank: 30,
    });
  }

  // 垂直切换线 + 标注 (仅分段模式)
  if (hasCombined && normalizedCombined!.switchTimeH > 0) {
    shapes.push({
      type: 'line',
      x0: normalizedCombined!.switchTimeH, x1: normalizedCombined!.switchTimeH,
      y0: 0, y1: 1, yref: 'paper',
      layer: 'above',
      line: { color: '#94A3B8', width: 1.5, dash: 'dot' },
    });
    annotations.push({
      x: normalizedCombined!.switchTimeH, y: 1, yref: 'paper',
      text: '方案切换', showarrow: false,
      font: { size: 11, color: '#64748B' }, yanchor: 'bottom',
    });
  }

  const legendColumnCount = plotContainerWidth >= 900 ? 4 : plotContainerWidth >= 690 ? 3 : 2;
  const legendEntryWidth = 0.96 / legendColumnCount;
  const legendItemCount = traces.filter((trace) => trace.showlegend !== false && trace.name).length;
  const legendRowCount = Math.max(1, Math.ceil(legendItemCount / legendColumnCount));
  const baseLegendRows = showDeviationSegments ? 3 : 2;
  const extraLegendRows = Math.max(0, legendRowCount - baseLegendRows);
  const extraLegendHeight = extraLegendRows * 20;

  const layout = {
    title: { text: '药物浓度-时间曲线', font: { size: 15, color: '#0B2545' } },
    xaxis: {
      title: { text: '时间 (h)', font: { color: '#5A6577', size: 12 } },
      gridcolor: '#EEF1F6', gridwidth: 1,
      linecolor: '#D1D9E6', zeroline: false,
      ...(autoScale ? { autorange: true } : { range: [tMin, tMax] }),
      tickmode: 'linear' as const,
      tick0: timeTickStart,
      dtick: timeTickStep,
      tickfont: { color: '#5A6577', size: 11 },
    },
    yaxis: {
      title: { text: `浓度 (${concentrationUnit})`, font: { color: '#5A6577', size: 12 } },
      gridcolor: '#EEF1F6', gridwidth: 1,
      linecolor: '#D1D9E6', zerolinecolor: '#D1D9E6',
      ...(autoScale ? { autorange: true } : { range: [0, yAxisUpper] }),
      tickfont: { color: '#5A6577', size: 11 },
    },
    showlegend: true,
    legend: {
      orientation: 'h' as const, x: 0, xanchor: 'left' as const,
      y: -0.24, yanchor: 'top' as const,
      entrywidth: legendEntryWidth,
      entrywidthmode: 'fraction' as const,
      traceorder: 'normal' as const,
      itemsizing: 'constant' as const,
      itemwidth: PK_CURVE_LEGEND_ITEM_WIDTH,
      font: { size: PK_CURVE_LEGEND_FONT_SIZE, color: '#334155' },
      bgcolor: 'rgba(248,250,252,0.88)',
      bordercolor: '#EEF1F6', borderwidth: 1,
    },
    hovermode: 'x unified' as const,
    hoverlabel: {
      bgcolor: 'rgba(255,255,255,0.92)',
      bordercolor: 'rgba(11,37,69,0.15)',
      font: { color: '#1A2332', size: 12 },
      namelength: -1,
    },
    shapes,
    annotations,
    plot_bgcolor: '#FAFBFC',
    paper_bgcolor: '#FFFFFF',
    margin: { t: 50, b: (showDeviationSegments ? 142 : 118) + extraLegendHeight, l: 60, r: 30 },
    height: (showDeviationSegments ? 448 : 418) + extraLegendHeight,
  };

  return (
    <div ref={plotContainerRef} style={{ width: '100%' }}>
      <Plot
        data={traces}
        layout={layout}
        config={{ responsive: true, displayModeBar: true }}
        useResizeHandler
        style={{ width: '100%' }}
      />
    </div>
  );
};

export default PKCurve;
